import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { FileText, Plus, Trash2 } from 'lucide-react';

/**
 * RulesTab — priority-ordered list of natural-language rules that shape
 * the AI Employee's behaviour. Split out of ChatbotDetailPage in iter119.
 *
 * All state + mutation handlers live in the parent (ChatbotDetailPage)
 * because rule ordering + editing state is shared with the persistence
 * layer. This component is purely presentational.
 */
export const RulesTab = ({
  rules,
  newRuleText,
  setNewRuleText,
  editingRule,
  setEditingRule,
  handleAddRule,
  handleUpdateRule,
  handleToggleRule,
  handleDeleteRule,
  handleMoveRule,
}) => {
  return (
    <Card className="bg-white border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="text-slate-900 flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-600" />
          AI Employee Rules ({rules.length})
        </CardTitle>
        <p className="text-sm text-slate-600 mt-1">
          Define rules in conversational language to control how your AI agent responds. Rules are applied in order of priority.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add new rule */}
        <div className="flex gap-2">
          <Textarea
            value={newRuleText}
            onChange={(e) => setNewRuleText(e.target.value)}
            placeholder="Enter a rule in natural language, e.g., 'Always greet users by name if available' or 'Never discuss competitor products' or 'If asked about pricing, always offer to schedule a call'"
            className="bg-white border-slate-300 text-slate-900 resize-none flex-1 placeholder:text-slate-400"
            rows={2}
            data-testid="new-rule-input"
          />
          <Button
            onClick={handleAddRule}
            disabled={!newRuleText.trim()}
            className="bg-blue-600 hover:bg-blue-700 self-end"
            data-testid="add-rule-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Rule
          </Button>
        </div>

        {/* Rules list */}
        {rules.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-slate-300 rounded-lg">
            <FileText className="w-12 h-12 text-slate-400 mx-auto mb-3" />
            <p className="text-slate-600 mb-2">No rules defined yet</p>
            <p className="text-sm text-slate-500">Add rules above to control your AI agent&apos;s behavior</p>
          </div>
        ) : (
          <div className="space-y-3">
            {rules.map((rule, index) => (
              <div
                key={rule.rule_id}
                className={`p-4 rounded-lg border transition-all ${
                  rule.is_active
                    ? 'bg-slate-50 border-slate-200'
                    : 'bg-slate-50/50 border-slate-200/50 opacity-60'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-xs text-slate-500 font-medium">#{index + 1}</span>
                      <button onClick={() => handleMoveRule(rule.rule_id, 'up')} className="text-slate-400 hover:text-slate-900 p-1" disabled={index === 0}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                      </button>
                      <button onClick={() => handleMoveRule(rule.rule_id, 'down')} className="text-slate-400 hover:text-slate-900 p-1" disabled={index === rules.length - 1}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </button>
                    </div>

                    {editingRule === rule.rule_id ? (
                      <div className="flex-1 space-y-2">
                        <Textarea defaultValue={rule.content} className="bg-white border-slate-300 text-slate-900 resize-none w-full" rows={2} id={`edit-rule-${rule.rule_id}`} />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => {
                              const textarea = document.getElementById(`edit-rule-${rule.rule_id}`);
                              handleUpdateRule(rule.rule_id, textarea.value);
                            }}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            Save
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingRule(null)} className="border-slate-300 text-slate-700">Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-slate-700 flex-1">{rule.content}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleRule(rule.rule_id)}
                      className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                        rule.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'
                      }`}
                    >
                      {rule.is_active ? 'Active' : 'Inactive'}
                    </button>
                    <Button variant="ghost" size="icon" onClick={() => setEditingRule(rule.rule_id)} className="text-slate-500 hover:text-slate-900">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteRule(rule.rule_id)} className="text-slate-500 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {rules.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-700">
              <strong>Tip:</strong> Rules are processed from top to bottom. Higher priority rules (top) take precedence.
              Use clear, conversational language like &quot;Always...&quot;, &quot;Never...&quot;, &quot;If... then...&quot;, or &quot;When asked about...&quot;
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RulesTab;
